import axios from "axios";

import { LOCALSTORAGE_KEYS } from "@/config";
import { ENDPOINTS, API_BASE_URL } from "@/config/api.enpoints";
import useAuthStore from "@/store/useAuthStore";

const url = import.meta.env.VITE_ENV_API_BASEURL || "http://localhost:5000"; // Corrected environment variable name
axios.defaults.withCredentials = true;

const apiClient = axios.create({
  baseURL: url,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Attach token to each request
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(LOCALSTORAGE_KEYS.token);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Extend AxiosRequestConfig to allow custom _retry flag
declare module "axios" {
  export interface AxiosRequestConfig {
    _retry?: boolean;
  }
}
// Attach response interceptor
import { jwtDecode } from "jwt-decode";
interface JwtPayload {
  name: string;
  role: string;
  id: string;
  tenantid: string;
}

apiClient.interceptors.response.use(
  (response) => {
    const newToken = response.headers["x-new-token"];
    if (newToken) {
      localStorage.setItem(LOCALSTORAGE_KEYS.token, newToken);
      apiClient.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
      try {
        const decoded = jwtDecode<JwtPayload>(newToken);
        const store = useAuthStore.getState();
        store.setRole(decoded.role);
        store.setTenantId(decoded.tenantid);
        store.setUserName(decoded.name);
        store.setId(decoded.id);
      } catch (decodeErr) {
        console.error("Failed to decode refreshed token:", decodeErr);
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const newToken = error.response?.headers?.["x-new-token"];
    if (newToken) {
      localStorage.setItem(LOCALSTORAGE_KEYS.token, newToken);
      apiClient.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
      try {
        const decoded = jwtDecode<JwtPayload>(newToken);
        const store = useAuthStore.getState();
        store.setRole(decoded.role);
        store.setTenantId(decoded.tenantid);
        store.setUserName(decoded.name);
        store.setId(decoded.id);
      } catch (decodeErr) {
        console.error("Failed to decode refreshed token:", decodeErr);
      }
    }
    const isRefreshRequest = originalRequest?.url?.includes(ENDPOINTS.AUTH.refresh);

    if (error.response?.status === 401 && !originalRequest._retry && !isRefreshRequest) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem(LOCALSTORAGE_KEYS.refreshToken);
        if (!refreshToken) {
          useAuthStore.getState().logout();
          window.location.href = "/login";
          return Promise.reject(error);
        }

        const res = await apiClient.post(`${API_BASE_URL}${ENDPOINTS.AUTH.refresh}`, {
          refreshToken,
        });

        const freshToken = res.data?.data?.token || res.data?.data || res.data?.accessToken;
        if (freshToken) {
          localStorage.setItem(LOCALSTORAGE_KEYS.token, freshToken);
          apiClient.defaults.headers.common["Authorization"] = `Bearer ${freshToken}`;
          originalRequest.headers["Authorization"] = `Bearer ${freshToken}`;

          try {
            const decoded = jwtDecode<JwtPayload>(freshToken);
            const store = useAuthStore.getState();
            store.setRole(decoded.role);
            store.setTenantId(decoded.tenantid);
            store.setUserName(decoded.name);
            store.setId(decoded.id);
          } catch (decodeErr) {
            console.error("Failed to decode refreshed token:", decodeErr);
          }

          return apiClient(originalRequest); // Retry original request with fresh token
        }
      } catch (err) {
        // Refresh token failed or expired -> force logout
        useAuthStore.getState().logout();
        window.location.href = "/login";
        return Promise.reject(err);
      }
    }

    // Ensure we provide a user-friendly error message if available from the backend
    if (error.response?.data?.message) {
      error.message = error.response.data.message;
    } else if (error.response?.data?.error) {
      error.message = error.response.data.error;
    } else if (!error.response) {
      error.message = 'Network error or server is unreachable. Please try again.';
    } else if (error.response?.status >= 500) {
      error.message = 'An unexpected server error occurred. Please try again later.';
    }

    return Promise.reject(error); // Pass other errors through
  },
);

export default apiClient;
